<script setup lang="ts">
import $ from 'jquery';
import { nextTick, ref, shallowRef, unref, watch } from 'vue';

import { useGenerateIds, useJqueryEventListener } from '#public_ts/composables';

import type { TitleType } from './bootstrap-panel';


const props = defineProps<{
    title?: string;
    //h1 is centered, div is not, default is h4
    titleType?: TitleType;
    initiallyCollapsed?: boolean;
    collapsed?: boolean;
    accordion?: string;
    unloadCollapsed?: boolean;
}>();
const emit = defineEmits<{
    (event: 'update:collapsed', value: boolean | undefined): void;
}>();
const collapseElement = shallowRef<HTMLElement | undefined>();
const generateIds = useGenerateIds();
const headingId = generateIds();
const collapseId = generateIds();
const isCollapsed = ref(props.initiallyCollapsed ?? props.collapsed ?? false);
const isDestroyCollapsed = ref(isCollapsed.value);

function makeCollapsed(val: boolean) {
    const element = $(unref(collapseElement) ?? []);
    if(!element.length) return;
    if(val) {
        element.collapse('hide');
    } else {
        element.collapse('show');
    }
}

useJqueryEventListener(collapseElement, 'show.bs.collapse', (e) => {
    if(props.unloadCollapsed && unref(isDestroyCollapsed)) {
        e.preventDefault();
        isDestroyCollapsed.value = false;
        nextTick(() => {
            makeCollapsed(false);
        });
    }
});
useJqueryEventListener(collapseElement, 'shown.bs.collapse', () => {
    isCollapsed.value = false;
    emit('update:collapsed', false);
});
useJqueryEventListener(collapseElement, 'hidden.bs.collapse', () => {
    isCollapsed.value = true;
    isDestroyCollapsed.value = true;
    emit('update:collapsed', true);
});
watch(() => props.collapsed, (val) => {
    makeCollapsed(val);
});
</script>
<template>
    <div class="panel panel-default">
        <div :id="headingId" class="panel-heading">
            <Component :is="props.titleType || 'h4'" class="panel-title d-flex flex-row">
                <slot name="title-before" />
                <a
                    role="button"
                    data-toggle="collapse"
                    :data-parent="props.accordion"
                    :href="`#${collapseId}`"
                    :aria-expanded="isCollapsed ? undefined : 'true'"
                    :aria-controls="collapseId"
                >
                    <slot name="title">
                        {{ props.title }}
                    </slot>
                    <span class="ms-2" :class="isCollapsed ? 'bi bi-chevron-down' : 'bi bi-chevron-up'" />
                </a>
                <slot name="title-after" />
            </Component>
        </div>
        <div
            :id="collapseId"
            ref="collapseElement"
            class="panel-collapse collapse"
            :class="{ in: !isCollapsed }"
            role="tabpanel"
            :aria-labelledby="headingId"
        >
            <div v-if="!props.unloadCollapsed || !isDestroyCollapsed" class="panel-body">
                <slot />
            </div>
        </div>
    </div>
</template>
